/* eslint-disable max-len */
import React, { FC } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { LFC } from '../../../Legacy';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { stringStep } from '../../../concepts/commands/CommandWizard';
import { ObjectManager } from '../../../concepts/objects';
import { Button, Colors, Prompt } from '../../../ui';
import { EditorSession } from '../../canvas/EditorSession';
import { Selection } from '../../canvas/Selection';
import { resolvePhysical } from '../../canvas/commands/CommandUtil';
import { place } from '../../canvas/commands/OrderCommands';
import { RelativeTo } from '../../canvas/entities/Physical';
import { ContentInjector } from '../../canvas/injection/ContentInjector';
import { ContentPayload } from '../../canvas/injection/ContentPayload';
import { Popup, PopupProps } from '../../popups/Popup';
import { PopupManager } from '../../popups/PopupManager';
import { alert } from '../alerts/AlertCommands';
import { AutoLayout } from '../autoLayout/AutoLayout';
import { autoLayout } from '../autoLayout/AutoLayoutCommands';
import { FlexLayout } from '../autoLayout/FlexLayout';

const getColor = (props) => {
    if (props.isDragAccept) {
        return Colors.positive;
    }
    if (props.isDragReject) {
        return Colors.negative;
    }
    if (props.isDragActive) {
        return Colors.primary;
    }
    return Colors.active;
};

const DropFrame = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  border-width: .125rem;
  border-radius: .125rem;
  border-color: ${props => getColor(props)};
  border-style: dashed;
  background-color: #F6FAFD;
  color: ${Colors.textLight};
  outline: none;
  transition: border .24s ease-in-out;
`;

const DropZone: FC<{ maxFiles: number, onDrop: (files: File[]) => void }> = ({ maxFiles, onDrop }) => {

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject
    } = useDropzone({
        maxFiles,
        onDrop: files => onDrop(files),
    });

    return <DropFrame {...getRootProps({ isDragActive, isDragAccept, isDragReject })}>
        <input {...getInputProps()} />
        <p>Drag &amp; drop a CSV file here, or click to select files.</p>
        <br />
        <Button variant="primary">Browse...</Button>
    </DropFrame>;
};

export const importDataFromCsvFile = defineCommand(
    'importDataFromCsvFile',
    'Import: From CSV File...',
    async (cc, [container]: [any?]) => {

        const injector = cc.resolve(ContentInjector);
        const objects = cc.resolve(ObjectManager);
        const session = cc.resolve(EditorSession);

        const { actioned, data } = await cc.call(PopupManager).invoke(importDataFromCsvFilePopup());
        if (!actioned && !data) return;
        const file = data as File;
        const type = file.type.toLowerCase();

        if (type === 'text/plain' || type.includes('csv')) {

            const payload = ContentPayload.create(file.type, file, { method: 'popup' });
            const ok = await injector.inject(payload);
            if (!ok) return;

            objects.update(() => {

                const cont = resolvePhysical(cc, container);
                if (!!cont) {

                    const elmts = session.get(Selection).toArray();
                    FlexLayout.exec(cont.bounds(RelativeTo.Model), ['row', 'start', 'start', 10], elmts);
                    cc.dispatch(place, [elmts.map(x => x.id)]);

                    if (cont.is(AutoLayout)) {
                        cc.dispatch(autoLayout, [cont.id]);
                    }
                }
            });
        }
        else {
            cc.dispatch(alert, ['Doesn\'t look like a CSV file, please use a CSV file!', 'auto', 'error']);
        }

    },
    [
        stringStep('container', 'Container:')
    ]
);

export const importDataFromCsvFilePopup = () => ({ ui: ImportDataFromCsvFilePopup, droppable: true });

export const ImportDataFromCsvFilePopup: LFC<PopupProps> = ({ action, dismiss }) =>
    <Prompt ignoreEnter onAction={dismiss}>
        <Popup w={48} h={36}>
            <Popup.Close onAction={dismiss} />
            <Popup.Title>Import From CSV File</Popup.Title>
            <Popup.Content>
                <DropZone maxFiles={1} onDrop={files => action(files[0])} />
            </Popup.Content>
            <Popup.Actions>
                <Button onAction={dismiss}>Close</Button>
            </Popup.Actions>
        </Popup>
    </Prompt>;


